import {
    useContext,
    type FC,
    useId
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type INavigationMenuLinkProps from "./type";
import type {
    NavigationMenuLinkWebPropsType
} from "./type";
import stylesheet, {
    NAVIGATION_MENU_LINK_LABEL_VARIANT
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    INTERACTION_STATE_DATA_KEY,
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import optionRowStylesheet, {
    OPTION_ROW_DESCRIPTION_VARIANT,
    useStyles as useOptionRowStyles,
    getOptionRowMeasures,
    getOptionRowColors,
    getOptionRowRadius
} from "../../../optionRow/stylesheet";
import {
    NavigationMenuLayoutContext,
    useNavigationMenuContext
} from "../../context";
import {
    getItemTitleVariant
} from "../../../item/stylesheet";
import {
    openLink
} from "../../../../helpers/link";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const NavigationMenuLink: FC<INavigationMenuLinkProps> = ({
    customTheme: customThemeProp,
    icon: IconComponentProp,
    isDisabled = false,
    isActive = false,
    subTitle,
    onPress,
    style,
    title,
    href
}) => {
    const context = useNavigationMenuContext({
        part: "Link"
    });

    const layout = useContext(NavigationMenuLayoutContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const baseId = useId();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled
    });

    const descriptionId = `${baseId}-description`;

    const variant = layout.isSheet ? "sheet" : "popover";

    const isDescriptionVisible = !!subTitle;

    const isSelectedToneVisible = isActive && !isDisabled;

    const isLayerHovered = !isDisabled && (isHovered || isFocusVisible);

    const isLayerPressed = !isDisabled && isPressed;

    const currentMeasures = getOptionRowMeasures({
        isDescriptionVisible,
        controlHeights,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        variant,
        spaces
    });

    const currentRadius = getOptionRowRadius({
        minHeight: currentMeasures.minHeight,
        radiuses,
        variant,
        borders,
        spaces
    });

    const currentColors = getOptionRowColors({
        isLayered: isLayerHovered || isLayerPressed,
        isSelectedToneVisible,
        type: "neutral",
        isDisabled,
        colors
    });

    const {
        textColumn: textColumnDynamicStyle,
        container: containerDynamicStyle,
        iconSlot: iconSlotDynamicStyle
    } = useOptionRowStyles({
        isFocusRingVisible: isFocusVisible,
        borderRadius: currentRadius,
        isMetaVisible: false,
        currentMeasures,
        type: "neutral",
        currentColors,
        shapeConfigs,
        isDisabled,
        borders,
        colors,
        spaces
    });

    if(!context) {
        return null;
    }

    const handlePress = () => {
        onPress?.();

        context.handleLinkPress();

        openLink({
            href
        });
    };

    const webProps: NavigationMenuLinkWebPropsType = Platform.OS === "web" ? {
        "aria-describedby": isDescriptionVisible ? descriptionId : undefined,
        "aria-current": isSelectedToneVisible ? "page" : undefined,
        dataSet: isDisabled ? {
            [INTERACTION_STATE_DATA_KEY]: "disabled"
        } : interactionHandlers.dataSet,
        href: isDisabled ? undefined : href
    } : {};

    const renderIcon = () => {
        if(!IconComponentProp) {
            return null;
        }

        return <View
            style={[
                optionRowStylesheet.iconSlot,
                iconSlotDynamicStyle
            ]}
            aria-hidden={true}
        >
            <IconComponentProp
                customColor={currentColors.iconColor}
                color={currentColors.iconColorKey}
                size={currentMeasures.iconSize}
            />
        </View>;
    };

    const renderLabel = () => {
        return <Text
            style={isDescriptionVisible ? undefined : stylesheet.label}
            variant={getItemTitleVariant({
                titleVariant: NAVIGATION_MENU_LINK_LABEL_VARIANT,
                isSelected: isSelectedToneVisible,
                size: "medium"
            })}
            customColor={currentColors.labelColor}
            customTheme={customTheme}
            numberOfLines={1}
        >
            {title}
        </Text>;
    };

    const renderText = () => {
        if(!isDescriptionVisible) {
            return renderLabel();
        }

        return <View
            style={[
                optionRowStylesheet.textColumn,
                textColumnDynamicStyle
            ]}
        >
            {renderLabel()}
            <Text
                customColor={isDisabled ? colors.content.text.mid : currentColors.descriptionColor}
                variant={OPTION_ROW_DESCRIPTION_VARIANT}
                customTheme={customTheme}
                id={descriptionId}
                numberOfLines={2}
            >
                {subTitle}
            </Text>
        </View>;
    };

    return <View
        style={stylesheet.item}
        role="listitem"
    >
        <Pressable
            {...interactionHandlers}
            {...webProps}
            aria-selected={Platform.OS === "web" ? undefined : isActive}
            onPress={isDisabled ? undefined : handlePress}
            style={[
                style,
                optionRowStylesheet.container,
                containerDynamicStyle
            ]}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            role="link"
        >
            <StateLayer
                overlayColor={currentColors.overlayColor}
                borderRadius={currentRadius}
                isHovered={isLayerHovered}
                isPressed={isLayerPressed}
                customTheme={customTheme}
            />
            {renderIcon()}
            {renderText()}
        </Pressable>
    </View>;
};
export default NavigationMenuLink;
